import React, { useState } from 'react';
import { Calendar, Plus, Trash2, Info, Download, X, BookOpen, GraduationCap } from 'lucide-react';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Button from '../ui/Button';
import ResultGauge from '../ui/ResultGauge';
import { calculateSemesterGPA } from '../../utils/gpaLogic';
import { generateSemesterPDF } from '../../utils/pdfGenerator';
import { toast } from 'react-toastify';
import logo from '../../logo1.png';

const SemesterCalculator = () => {
  const [showHelp, setShowHelp] = useState(true);
  const [subjects, setSubjects] = useState([{ name: '', creditHours: '', subjectGPA: '' }]);
  const [result, setResult] = useState(null);

  const addSubject = () => {
    setSubjects([...subjects, { name: '', creditHours: '', subjectGPA: '' }]);
  };

  const removeSubject = (index) => {
    if (subjects.length > 1) {
      setSubjects(subjects.filter((_, i) => i !== index));
    }
  };

  const updateSubject = (index, field, value) => {
    const newSubjects = [...subjects];
    newSubjects[index][field] = value;
    setSubjects(newSubjects);
  };

  const handleCalculate = () => {
    const res = calculateSemesterGPA(subjects);
    if (res.totalCreditHours === 0) {
      toast.error("Please add at least one subject with valid data.");
    } else {
      setResult(res);
      toast.success("Calculated!");
    }
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    
    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const logoBase64 = canvas.toDataURL('image/png');
      
      generateSemesterPDF(subjects, result, logoBase64);
      toast.success("PDF Downloaded");
    };
    img.onerror = () => {
      generateSemesterPDF(subjects, result, null);
      toast.success("PDF Downloaded");
    };
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {/* Helper Banner */}
      {showHelp && (
        <div className="p-5 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-[2rem] flex items-start gap-4 relative transition-all group">
          <div className="p-2 bg-white dark:bg-[#0f1535] rounded-xl text-indigo-600 shadow-sm flex-shrink-0">
            <Info size={20} />
          </div>
          <div className="text-sm">
            <p className="font-extrabold text-slate-900 dark:text-white uppercase tracking-tight mb-1">Quick Tip</p>
            <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
              Enter the <span className="text-indigo-600 dark:text-indigo-400 font-bold">GPA (0-4.0)</span> for each subject. Need help calculating subject GPA? Use the Theory or Lab tools first!
            </p>
          </div>
          <button
            onClick={() => setShowHelp(false)}
            className="absolute top-4 right-4 text-slate-300 hover:text-rose-500 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Subjects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {subjects.map((sub, index) => (
          <div 
            key={index} 
            className="group bg-white dark:bg-[#0f1535] p-5 rounded-[2.5rem] border border-slate-100 dark:border-indigo-500/10 shadow-sm hover:shadow-xl hover:shadow-indigo-500/5 transition-all duration-300 animate-in slide-in-from-bottom-4"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center text-[10px] font-black text-slate-400 dark:text-slate-500">
                  {index + 1}
                </div>
                <span className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Subject</span>
              </div>
              <button 
                onClick={() => removeSubject(index)}
                className="opacity-0 group-hover:opacity-100 p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-xl transition-all"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="space-y-4">
              <Input 
                value={sub.name}
                onChange={(e) => updateSubject(index, 'name', e.target.value)}
                placeholder="Course Name"
                className="font-bold border-transparent bg-slate-50 dark:bg-white/5"
              />
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Credits</label>
                  <Input 
                    value={sub.creditHours}
                    onChange={(e) => updateSubject(index, 'creditHours', e.target.value)}
                    placeholder="0"
                    inputMode="numeric"
                    className="text-center font-bold"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">GPA</label>
                  <Input 
                    value={sub.subjectGPA}
                    onChange={(e) => updateSubject(index, 'subjectGPA', e.target.value)}
                    placeholder="0.00"
                    inputMode="decimal"
                    className="text-center font-bold"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Add Subject Card */}
        <button
          onClick={addSubject}
          className="h-full min-h-[160px] flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-200 dark:border-indigo-500/20 rounded-[2.5rem] hover:border-indigo-500 dark:hover:border-indigo-400 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/5 group transition-all"
        >
          <div className="p-3 bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-500 group-hover:bg-indigo-600 group-hover:text-white rounded-2xl mb-3 transition-all">
            <Plus size={24} />
          </div>
          <span className="font-black text-slate-400 dark:text-slate-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 uppercase tracking-widest text-xs">Add Subject</span>
        </button>
      </div>

      <div className="flex flex-col items-center gap-8 pt-4">
        <Button 
          onClick={handleCalculate} 
          className="w-full md:w-auto px-16 py-4 rounded-2xl shadow-lg shadow-indigo-500/20"
          icon={GraduationCap}
        >
          Calculate Semester GPA
        </Button>

        {result && (
          <div className="w-full max-w-lg animate-in zoom-in-95 duration-500">
            <div className="bg-white dark:bg-[#0b0f2a] p-8 rounded-[3rem] border border-slate-100 dark:border-indigo-500/10 shadow-2xl relative overflow-hidden flex flex-col items-center">
              <ResultGauge 
                value={result.gpa} 
                label="Semester GPA" 
                subLabel={`${subjects.length} Subjects Total`}
                size={240}
              />
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4 w-full pt-8 border-t border-slate-100 dark:border-indigo-500/5">
                <div className="flex-1 text-center py-2 px-4 bg-slate-50 dark:bg-white/5 rounded-2xl">
                  <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Total Quality Points</span>
                  <span className="text-xl font-bold text-slate-900 dark:text-white">{(result.gpa * result.totalCreditHours).toFixed(2)}</span>
                </div>
                <div className="flex-1 text-center py-2 px-4 bg-slate-50 dark:bg-white/5 rounded-2xl">
                  <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Total Credit Hours</span>
                  <span className="text-xl font-bold text-slate-900 dark:text-white">{result.totalCreditHours}</span>
                </div>
              </div>

              <div className="mt-8 w-full">
                <Button 
                    onClick={handleDownloadPDF}
                    variant="secondary"
                    icon={Download}
                    className="w-full py-4 rounded-2xl text-xs uppercase tracking-[0.2em] font-black"
                  >
                    Download Report PDF
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SemesterCalculator;
